import { dec } from '@app/shared';
import type { DocumentListItem, DocumentType } from '@/entities/document';
import type { PaymentAllocationDto } from '@/entities/payment';

export type PaymentFormType = 'PAYMENT' | 'REFUND' | 'OFFSET';

export interface AllocationTarget {
  documentType: DocumentType;
  /** Префикс номера в сетке: «С-12/2026». */
  prefix: string;
  title: string;
  /** Заголовок колонки остатка: у долга — «Долг», у возврата — сколько ещё не закрыто. */
  remainderTitle: string;
}

/**
 * §7: на что разносится платёж. Обычная оплата — на долги своей стороны (получили — продажи,
 * отдали — приходы). Возврат денег — на документы возврата той стороны, чьи деньги возвращаются:
 * отдали клиенту — его возвраты нам, получили от поставщика — наши возвраты ему.
 */
export function allocationTarget(type: PaymentFormType, direction: 'IN' | 'OUT'): AllocationTarget {
  if (type === 'REFUND') {
    return direction === 'OUT'
      ? { documentType: 'RETURN_IN', prefix: 'ВК', title: 'Разнести на возвраты от клиента', remainderTitle: 'Не закрыто' }
      : { documentType: 'RETURN_OUT', prefix: 'ВП', title: 'Разнести на возвраты поставщику', remainderTitle: 'Не закрыто' };
  }
  return direction === 'IN'
    ? { documentType: 'SALE', prefix: 'С', title: 'Разнести на продажи', remainderTitle: 'Долг' }
    : { documentType: 'PURCHASE', prefix: 'П', title: 'Разнести на приходы', remainderTitle: 'Долг' };
}

/** Ключ пересева сетки: меняется, когда сервер отдал другой набор документов, другую оплату по
 * ним, другое разнесение или новую версию платежа — то есть после любого сохранения, а не только
 * смены списка. */
export function allocationSeedKey(scope: string, paymentRowVersion: number | undefined, items: DocumentListItem[], allocations: PaymentAllocationDto[] = []): string {
  const documents = items.map((i) => `${i.id}=${i.paidAmount}`).join(',');
  const own = allocations.map((a) => `${a.documentId}=${a.amount}`).join(',');
  return `${scope}:${paymentRowVersion ?? 0}:${documents}:${own}`;
}

export interface AllocationRow {
  documentId: string;
  label: string;
  debt: string;
  amount: string | null;
}

/** Строки грида разнесения — неоплаченные документы нужной стороны (§7). */
export function rowsFromDocuments(items: DocumentListItem[], prefix: string, preset?: Map<string, string>): AllocationRow[] {
  return items.map((d) => ({
    documentId: d.id,
    label: `${prefix}-${d.number}/${d.numberYear}`,
    debt: dec(d.totalAmount).minus(dec(d.paidAmount)).toFixed(2),
    amount: preset?.get(d.id) ?? null,
  }));
}

/**
 * Сетка разнесения существующего платежа (§7 «владелец может изменить разнесение вручную в
 * карточке платежа»): все документы, на которые платёж уже разнесён, — включая закрытые им
 * полностью, которых нет среди неоплаченных, — и остальные неоплаченные документы этой стороны.
 * «Долг» — долг документа без этого платежа, то есть наибольшая сумма, которую на него можно
 * отнести; по порядку документов (дата, номер) — так же, как платёж разносится сам.
 */
export function allocationRows(
  unpaid: DocumentListItem[],
  allocations: PaymentAllocationDto[],
  target: Pick<AllocationTarget, 'documentType' | 'prefix'>,
  prefillDocumentId?: string,
  prefillAmount?: string | null,
): AllocationRow[] {
  const own = new Map<string, ReturnType<typeof dec>>();
  const documents = new Map<string, { id: string; number: number; numberYear: number; docDate: string; totalAmount: string; paidAmount: string }>();
  for (const allocation of allocations) {
    if (!allocation.document || allocation.document.type !== target.documentType) continue;
    own.set(allocation.documentId, (own.get(allocation.documentId) ?? dec(0)).plus(dec(allocation.amount)));
    documents.set(allocation.documentId, allocation.document);
  }
  for (const item of unpaid) documents.set(item.id, item);

  return [...documents.values()]
    .sort((a, b) => a.docDate.slice(0, 10).localeCompare(b.docDate.slice(0, 10)) || a.numberYear - b.numberYear || a.number - b.number)
    .map((d) => {
      const allocated = own.get(d.id);
      const debt = dec(d.totalAmount).minus(dec(d.paidAmount)).plus(allocated ?? dec(0));
      const prefilled = prefillDocumentId === d.id && prefillAmount ? prefillAmount : null;
      return {
        documentId: d.id,
        label: `${target.prefix}-${d.number}/${d.numberYear}`,
        debt: debt.toFixed(2),
        amount: prefilled ?? (allocated ? allocated.toFixed(2) : null),
      };
    });
}

export function sumAmounts(rows: AllocationRow[]): string {
  return rows.reduce((sum, r) => sum.plus(dec(r.amount ?? '0')), dec(0)).toFixed(2);
}

export function toManualAllocations(rows: AllocationRow[]): { documentId: string; amount: string }[] {
  return rows.filter((r) => r.amount && dec(r.amount).gt(0)).map((r) => ({ documentId: r.documentId, amount: r.amount! }));
}

export function offsetIsBalanced(amount: string, receivable: AllocationRow[], payable: AllocationRow[]): boolean {
  if (!amount || dec(amount).lte(0)) return false;
  return sumAmounts(receivable) === dec(amount).toFixed(2) && sumAmounts(payable) === dec(amount).toFixed(2);
}
